import type { ApolloError } from 'apollo-client';

import type { OnDemandVideos, Resolution } from 'bundles/clips/components/short-form-content/SFCVLPSharedTypes';
import type { ClipsVideoData_VideoAssetsFragment as VideoAssetsData } from 'bundles/clips/components/short-form-content/__generated__/ClipsVideoAssets';
import type { SFCVLPCourseMaterialsQuery_OnDemandLearnerMaterialsV1Resource_courses_elements_learnerMaterialItems_lectures as Item } from 'bundles/clips/components/short-form-content/__generated__/SFCVLPCourseMaterialsQuery';
import type { ClipsVideoData_VideoDataFragment as ShortFormContentData } from 'bundles/clips/components/short-form-content/__generated__/ShortFormContentVideoData';
import { constructLanguageMap } from 'bundles/common/utils/subtitleUtils';

export const extractNonVideoCourseItems = (items: Item[]) => {
  return items.filter((item) => item.contentSummary.__typename !== 'OnDemandLearnerMaterialItemsV1_lectureMember');
};

export function hasVideosFiltered(onDemandVideos?: OnDemandVideos): boolean {
  return (
    !onDemandVideos?.sources?.byResolution?.['360p']?.webMVideoUrl &&
    !onDemandVideos?.sources?.byResolution?.['360p']?.mp4VideoUrl &&
    !!onDemandVideos?.sources?.byResolution?.['360p']?.previewImageUrl
  );
}

export function hasUserEnrolledThroughMutation(onDemandVideos?: OnDemandVideos, lastOnDemandVideos?: OnDemandVideos) {
  return (
    // need to check if imageUrls are the same
    onDemandVideos?.sources?.byResolution['360p']?.previewImageUrl ===
      lastOnDemandVideos?.sources?.byResolution['360p']?.previewImageUrl &&
    hasVideosFiltered(lastOnDemandVideos) &&
    !hasVideosFiltered(onDemandVideos)
  );
}

function exists<TValue>(value: TValue | undefined | null): value is TValue {
  return value != null;
}

// copied from enterprise-legacy-learner-home (might want to bring this in)
export function filterExistsOrDefaultCopied<TElement>(
  array: (TElement | undefined | null)[] | undefined | null,
  defaultValue: TElement[] = []
): TElement[] {
  return array?.filter(exists) ?? defaultValue;
}

export function convertVideoAssetsToOnDemandVideo(videoAssets?: Omit<VideoAssetsData, '__typename'> | null) {
  const onDemandVideos: OnDemandVideos = { sources: { byResolution: {} }, subtitlesVtt: {} };

  if (videoAssets) {
    onDemandVideos.sources.byResolution = videoAssets.videoSourcesConnection?.edges.reduce(
      (result, currentResolution) => {
        let newResult: Record<string, Resolution> = {};
        if (currentResolution) {
          const { node } = currentResolution;
          newResult = { ...result, [node.id]: node };
        }
        return newResult;
      },
      {} as Record<string, Resolution>
    );

    const subtitlesVtt =
      videoAssets.subtitlesVttsConnection?.edges.reduce((result, currentSubtitle) => {
        if (currentSubtitle) {
          const { node } = currentSubtitle;
          return { ...result, [node.id]: node.sourcePath };
        }
        return result;
      }, {} as Record<string, string>) ?? {};

    // ToDo add the subtitlesAssetTags when the API is updated
    onDemandVideos.subtitlesVtt = constructLanguageMap(subtitlesVtt, null);
  }

  return onDemandVideos;
}

export const extractSFCData = (
  shortFormContentData: ShortFormContentData | null | undefined,
  gqlLoading: boolean,
  error?: ApolloError
) => {
  if (error) {
    // TODO: Should check for specific (ImpossibleToGetAnyVideoAssetsAccessException) if possible
    return {
      videoDataDone: true,
      userEnrolledIntoCourse: false,
      hasSFCVideoDataError: true,
    };
  }
  const likes = shortFormContentData?.likes;
  const views = shortFormContentData?.views;
  const courseVideos = shortFormContentData?.courseVideos;
  const courseLessons = shortFormContentData?.courseLessons;
  const preferredAvailableForEnrollmentProgram = shortFormContentData?.preferredAvailableForEnrollmentProgram;
  const suggestedToJoinProgram = shortFormContentData?.suggestedToJoinProgram;
  const userEnrolledIntoCourse = shortFormContentData?.userEnrolledIntoCourse;
  const accessProvidedViaProgram = shortFormContentData?.accessProvidedViaProgram;
  const summary = shortFormContentData?.summary;

  return {
    summary,
    likes,
    views,
    courseVideos,
    courseLessons,
    preferredAvailableForEnrollmentProgram,
    suggestedToJoinProgram,
    userEnrolledIntoCourse: !!userEnrolledIntoCourse,
    videoDataDone: !gqlLoading,
    accessProvidedViaProgram,
  };
};

export const getShortFormContentUrl = (
  courseSlug: string,
  itemId: string,
  programSlug: string | undefined,
  orgSlug: string | undefined
): string => {
  if (orgSlug) {
    return `/videos/${courseSlug}/${itemId}?org=${orgSlug}`;
  }
  if (programSlug) {
    return `/videos/${courseSlug}/${itemId}?program=${programSlug}`;
  }
  return `/videos/${courseSlug}/${itemId}`;
};

export const getProgramHomeUrl = (programSlug: string) => `/programs/${programSlug}`;

export const getOrgHomeUrl = (orgSlug: string) => `/programs/all/${orgSlug}`;
